import {
  blobToBase64,
  getBatchPaymentReportInvoicePdfPath,
  getInstallationOrderInvoicePdfPath,
  getRemovalOrderInvoicePdfPath,
  getSalesOrderInvoicePdfPath,
} from "utils/invoiceEmailHelpers";

/** Optional: wait/retry PDF generation right after payment (server may lag behind DB). */
export type OrderPdfFetchRetryOptions = {
  /** Attempts including the first try (default 10). */
  maxAttempts?: number;
  /** Wait before the first GET so payment persistence / PDF job can start (default 600ms). */
  initialDelayMs?: number;
  /** Extra wait between retries (default 750ms; scaled by attempt index). */
  retryDelayMs?: number;
  /** Abort one GET if it hangs (default 20000ms). */
  perFetchTimeoutMs?: number;
};

type InstallationOrderPdfAttachmentParams = {
  installationOrderId: string;
  token?: string | null;
  fileLabel?: string;
  apiBaseUrl?: string;
  pdfFetch?: OrderPdfFetchRetryOptions;
};

type SalesOrderPdfAttachmentParams = {
  salesOrderId: string;
  token?: string | null;
  fileLabel?: string;
  apiBaseUrl?: string;
  pdfFetch?: OrderPdfFetchRetryOptions;
};

type RemovalOrderPdfAttachmentParams = {
  removalOrderId: string;
  token?: string | null;
  fileLabel?: string;
  apiBaseUrl?: string;
  pdfFetch?: OrderPdfFetchRetryOptions;
};

const sanitizeFilePart = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/#/g, "")
    .replace(/[^\w.-]+/g, "_");

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Statuses where a PDF may not be ready yet after payment — worth retrying. */
const PDF_RETRYABLE_HTTP_STATUS = new Set([404, 408, 425, 429, 500, 502, 503]);

async function fetchOrderPdfBlobWithRetry(
  url: string,
  token: string,
  options?: OrderPdfFetchRetryOptions
): Promise<Blob | null> {
  const maxAttempts = Math.max(1, Math.min(20, options?.maxAttempts ?? 10));
  const initialDelayMs = Math.max(0, options?.initialDelayMs ?? 600);
  const retryDelayMs = Math.max(100, options?.retryDelayMs ?? 750);
  const perFetchTimeoutMs = Math.max(3000, options?.perFetchTimeoutMs ?? 20_000);

  if (initialDelayMs > 0) {
    await sleep(initialDelayMs);
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), perFetchTimeoutMs);
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${String(token || "")}`,
        },
        signal: controller.signal,
      });

      if (res.ok) {
        const blob = await res.blob();
        if (blob && blob.size > 0) {
          return blob;
        }
      } else if (!PDF_RETRYABLE_HTTP_STATUS.has(res.status)) {
        // Likely auth / bad id — do not spin
        return null;
      }
    } catch {
      // network or abort — retry
    } finally {
      clearTimeout(tid);
    }

    if (attempt < maxAttempts - 1) {
      await sleep(retryDelayMs * (attempt + 1));
    }
  }

  return null;
}

/**
 * Fetches installation-order PDF from API and returns a sendTriggerEmails attachment map.
 */
export async function buildInstallationOrderPdfAttachment(
  params: InstallationOrderPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    installationOrderId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(installationOrderId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(
    `${api}/installation-orders/${encodeURIComponent(id)}/pdf`,
    String(token || ""),
    pdfFetch
  );
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "order");
  return { [`Installation-Order-${safe}.pdf`]: b64 };
}

/**
 * Fetches sales-order PDF from API and returns a sendTriggerEmails attachment map.
 */
export async function buildSalesOrderPdfAttachment(
  params: SalesOrderPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    salesOrderId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(salesOrderId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(
    `${api}/sales-orders/${encodeURIComponent(id)}/pdf`,
    String(token || ""),
    pdfFetch
  );
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "order");
  return { [`Order-Summary-${safe}.pdf`]: b64 };
}

/**
 * Fetches sales-order **invoice** PDF from API (`/sales-orders/{id}/invoice-pdf`) for email attachment.
 */
export async function buildSalesOrderInvoicePdfAttachment(
  params: SalesOrderPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    salesOrderId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(salesOrderId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const path = getSalesOrderInvoicePdfPath(id);
  if (!path) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(`${api}${path}`, String(token || ""), pdfFetch);
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "order");
  return { [`Invoice-${safe}.pdf`]: b64 };
}

/**
 * Fetches installation-order **invoice** PDF from API (`/installation-orders/{id}/invoice-pdf`).
 */
export async function buildInstallationOrderInvoicePdfAttachment(
  params: InstallationOrderPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    installationOrderId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(installationOrderId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const path = getInstallationOrderInvoicePdfPath(id);
  if (!path) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(`${api}${path}`, String(token || ""), pdfFetch);
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "order");
  return { [`Invoice-${safe}.pdf`]: b64 };
}

/**
 * Fetches removal-order **invoice** PDF from API (`/removal-orders/{id}/invoice-pdf`).
 */
export async function buildRemovalOrderInvoicePdfAttachment(
  params: RemovalOrderPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    removalOrderId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(removalOrderId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const path = getRemovalOrderInvoicePdfPath(id);
  if (!path) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(`${api}${path}`, String(token || ""), pdfFetch);
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "order");
  return { [`Invoice-${safe}.pdf`]: b64 };
}

/**
 * Fetches removal-order PDF from API and returns a sendTriggerEmails attachment map.
 */
export async function buildRemovalOrderPdfAttachment(
  params: RemovalOrderPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    removalOrderId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(removalOrderId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(
    `${api}/removal-orders/${encodeURIComponent(id)}/pdf`,
    String(token || ""),
    pdfFetch
  );
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "order");
  return { [`Removal-Order-${safe}.pdf`]: b64 };
}

type BatchPaymentReportPdfAttachmentParams = {
  reportId: string;
  token?: string | null;
  fileLabel?: string;
  apiBaseUrl?: string;
  pdfFetch?: OrderPdfFetchRetryOptions;
};

/**
 * Fetches batch-payment report **invoice** PDF from API (`/batch-payments/reports/{id}/invoice-pdf`).
 */
export async function buildBatchPaymentReportInvoicePdfAttachment(
  params: BatchPaymentReportPdfAttachmentParams
): Promise<Record<string, string> | undefined> {
  const {
    reportId,
    token,
    fileLabel,
    apiBaseUrl = process.env.REACT_APP_API_URL || "",
    pdfFetch,
  } = params;

  const id = String(reportId || "").trim();
  if (!id) return undefined;
  const api = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!api) return undefined;

  const path = getBatchPaymentReportInvoicePdfPath(id);
  if (!path) return undefined;

  const blob = await fetchOrderPdfBlobWithRetry(`${api}${path}`, String(token || ""), pdfFetch);
  if (!blob) return undefined;

  const b64 = await blobToBase64(blob);
  const safe = sanitizeFilePart(fileLabel || id || "report");
  return { [`Invoice-${safe}.pdf`]: b64 };
}

